import React from 'react'
import {Form, Formik} from 'formik'
import {css} from '@emotion/core'
import styled from '@emotion/styled'

import PopUp from '../../PopUp/PopupBSide'
import Button from '../../../../designSystem/Button'
import Stack from '../../../../designSystem/helpers/Stack'
import {LanguageSelect} from './LanguageSelect'
import {CurrencySelect} from './CurrencySelect'

import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'
import {LanguageType} from '@daedalus/core/src/localization/types/LanguageType'
import {CurrencyType} from '@daedalus/core/src/localization/types/CurrencyType'

export type PreferencesFormValues = {
  language: string
  currency: string
}

interface Props {
  /** Whether the popup is open */
  isOpen: boolean
  /** The current selected language */
  currentLanguage: string
  /** A list of available languages */
  availableLanguages: LanguageType[]
  /** The current selected currency */
  currentCurrency: string
  /** A list of available currencies */
  currenciesList: CurrencyType[]
  /** List of popular currencies */
  popularCurrenciesList: CurrencyType[]
  /** Should it render the currency selector */
  hasCurrency?: boolean
  /** Callback for when the popup is closed */
  onClose: () => void
  /** Callback for when the form is sumbitted */
  onChangePreferences: (values: PreferencesFormValues) => void
  /** Callback for when the language select is clicked */
  onClickLanguage?: () => void
  /** Callback for when the currency select is clicked */
  onClickCurrency?: () => void
}

const SubTitleElement = styled('p')(
  ({theme}) => css`
    ${theme.typography.text.small}
    margin: 0 0 ${theme.layout.spacing.s600}px;
    text-align: center;
  `
)

/**
 * Language and currency selector popup
 */
export const PreferencesPopup = ({
  isOpen,
  currentLanguage,
  availableLanguages,
  currentCurrency,
  currenciesList,
  popularCurrenciesList,
  hasCurrency = true,
  onClose,
  onChangePreferences,
  onClickLanguage,
  onClickCurrency
}: Props) => {
  const initialValues: PreferencesFormValues = {
    language: currentLanguage,
    currency: currentCurrency
  }

  return (
    <PopUp
      isOpen={isOpen}
      closeAction={onClose}
      header={
        hasCurrency ? (
          <FormattedMessageWrapper
            id="preferencesPopup.title"
            defaultMessage="Language and currency"
          />
        ) : (
          <FormattedMessageWrapper
            id="preferencesPopup.languageTitle"
            defaultMessage="Language"
          />
        )
      }
    >
      <SubTitleElement>
        {hasCurrency ? (
          <FormattedMessageWrapper
            id="preferencesPopup.subtitle"
            defaultMessage="Choose your preferred language and currency."
          />
        ) : (
          <FormattedMessageWrapper
            id="preferencesPopup.languageSubtitle"
            defaultMessage="Choose your preferred language."
          />
        )}
      </SubTitleElement>
      <Formik initialValues={initialValues} onSubmit={onChangePreferences}>
        <Form>
          <Stack space="s500">
            <LanguageSelect
              fieldId="language"
              availableLanguages={availableLanguages}
              onClick={onClickLanguage}
            />
            {hasCurrency && (
              <CurrencySelect
                fieldId="currency"
                currenciesList={currenciesList}
                popularCurrenciesList={popularCurrenciesList}
                onClick={onClickCurrency}
              />
            )}
            <div>
              <Button fullWidth size="xl" type="submit">
                <FormattedMessageWrapper
                  id="preferencesPopup.button"
                  defaultMessage="Save"
                />
              </Button>
            </div>
          </Stack>
        </Form>
      </Formik>
    </PopUp>
  )
}
